// src/otlp.ts
import { decode as decodeCbor } from "cbor-x";
function hexFromBytes(bytes) {
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, "0");
  }
  return out;
}
function base64FromBytes(bytes) {
  const bufferCtor = globalThis.Buffer;
  if (bufferCtor) {
    return bufferCtor.from(bytes).toString("base64");
  }
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const btoaFn = globalThis.btoa;
  if (!btoaFn) {
    throw new Error("No base64 encoder available");
  }
  return btoaFn(binary);
}
function anyValueFromCborBytes(bytes) {
  const value = decodeCbor(bytes);
  return anyValueFromJs(value);
}
function anyValueFromJs(value) {
  if (value === null || value === void 0) {
    return { stringValue: "" };
  }
  if (typeof value === "string") {
    return { stringValue: value };
  }
  if (typeof value === "boolean") {
    return { boolValue: value };
  }
  if (typeof value === "number") {
    if (Number.isFinite(value) && Number.isInteger(value)) {
      return { intValue: value.toString() };
    }
    return { doubleValue: value };
  }
  if (typeof value === "bigint") {
    return { intValue: value.toString() };
  }
  if (value instanceof Uint8Array) {
    return { bytesValue: base64FromBytes(value) };
  }
  if (Array.isArray(value)) {
    return { arrayValue: { values: value.map((v) => anyValueFromJs(v)) } };
  }
  if (value instanceof Map) {
    const values = [];
    for (const [key, mapValue] of value.entries()) {
      if (typeof key !== "string") {
        continue;
      }
      values.push({ key, value: anyValueFromJs(mapValue) });
    }
    return { kvlistValue: { values } };
  }
  if (typeof value === "object") {
    const values = [];
    for (const [key, objectValue] of Object.entries(value)) {
      values.push({ key, value: anyValueFromJs(objectValue) });
    }
    return { kvlistValue: { values } };
  }
  return { stringValue: String(value) };
}

// src/read-range.ts
import { decode as decodeCbor2 } from "cbor-x";
function toUint8Array(buffer) {
  return new Uint8Array(buffer);
}
function normalizeBytes(input) {
  return input instanceof Uint8Array ? input : new Uint8Array(input);
}
function spanKey(spanId) {
  return hexFromBytes(normalizeBytes(spanId));
}
function readRangeWireToOtlp(wire, resource) {
  const startMs = typeof wire.startTimeMs === "bigint" ? wire.startTimeMs : BigInt(Math.floor(wire.startTimeMs));
  const endMs = typeof wire.endTimeMs === "bigint" ? wire.endTimeMs : BigInt(Math.floor(wire.endTimeMs));
  const limit = typeof wire.limit === "bigint" ? Number(wire.limit) : wire.limit;
  if (limit <= 0 || endMs <= startMs) {
    return { otlp: emptyExport(resource), clamped: wire.clamped };
  }
  const startNs = startMs * 1000000n;
  const endNs = endMs * 1000000n;
  const baseRecords = buildBaseRecordMap(wire.baseChunks);
  const sequenceRef = { value: 0 };
  const records = [];
  for (const chunk of wire.chunks) {
    collectRecordEntries(records, chunk, startNs, endNs, sequenceRef);
  }
  records.sort((a, b) => {
    if (a.absNs < b.absNs) return -1;
    if (a.absNs > b.absNs) return 1;
    return a.sequence - b.sequence;
  });
  const { spans, reachedSpanLimit } = buildSpansFromRecords(
    records,
    baseRecords,
    limit
  );
  const exported = spans.map(toOtlpSpan);
  return {
    otlp: buildExport(exported, resource),
    clamped: wire.clamped || reachedSpanLimit
  };
}
function collectRecordEntries(collector, chunk, startNs, endNs, sequenceRef) {
  for (const record of chunk.records) {
    const absNs = chunk.baseUnixNs + record.timeOffsetNs;
    if (absNs < startNs || absNs >= endNs) {
      continue;
    }
    collector.push({
      record,
      strings: chunk.strings,
      absNs,
      sequence: sequenceRef.value++
    });
  }
}
function buildBaseRecordMap(chunks) {
  const map = /* @__PURE__ */ new Map();
  for (const chunk of chunks) {
    for (const record of chunk.records) {
      const absNs = chunk.baseUnixNs + record.timeOffsetNs;
      map.set(spanKey(recordSpanId(record.body)), {
        record,
        strings: chunk.strings,
        absNs
      });
    }
  }
  return map;
}
function buildSpansFromRecords(records, baseRecords, limit) {
  const spans = /* @__PURE__ */ new Map();
  let reachedSpanLimit = false;
  for (const entry of records) {
    const body = entry.record.body;
    const id = recordSpanId(body);
    const key = spanKey(id);
    let span = spans.get(key);
    if (!span) {
      if (spans.size >= limit) {
        reachedSpanLimit = true;
        continue;
      }
      const baseRecord = baseRecords.get(key);
      if (baseRecord) {
        span = initSpanFromBaseRecord(
          baseRecord.record.body,
          baseRecord.strings,
          baseRecord.absNs
        );
      }
      if (!span) {
        span = initSpanFromRecord(
          body,
          entry.absNs,
          entry.strings
        );
      }
      if (!span) {
        continue;
      }
      spans.set(key, span);
    }
    applyRecord(span, body, entry.absNs, entry.strings);
  }
  return { spans: Array.from(spans.values()), reachedSpanLimit };
}
function recordSpanId(body) {
  switch (body.tag) {
    case "SpanStart":
      return body.val.spanId;
    case "SpanEvent":
      return body.val.spanId;
    case "SpanUpdate":
      return body.val.spanId;
    case "SpanEnd":
      return body.val.spanId;
    case "SpanSnapshot":
      return body.val.spanId;
  }
}
function initSpanFromBaseRecord(base, strings, absNs) {
  switch (base.tag) {
    case "SpanStart":
      return initSpanFromStart(base.val, absNs, strings);
    case "SpanSnapshot":
      return initSpanFromSnapshot(base.val, strings);
    default:
      return void 0;
  }
}
function initSpanFromRecord(body, absNs, strings) {
  switch (body.tag) {
    case "SpanStart":
      return initSpanFromStart(body.val, absNs, strings);
    case "SpanSnapshot":
      return initSpanFromSnapshot(body.val, strings);
    default:
      return void 0;
  }
}
function initSpanFromStart(start, absNs, strings) {
  return {
    traceId: start.traceId,
    spanId: start.spanId,
    parentSpanId: start.parentSpanId,
    name: strings[start.name] ?? "",
    kind: start.kind,
    traceState: start.traceState,
    flags: start.flags,
    attributes: decodeAttributeList(start.attributes, strings),
    droppedAttributesCount: start.droppedAttributesCount,
    links: decodeLinks(start.links, strings),
    droppedLinksCount: start.droppedLinksCount,
    status: null,
    startTimeUnixNs: absNs ?? 0n,
    endTimeUnixNs: null,
    events: []
  };
}
function initSpanFromSnapshot(snapshot, strings) {
  return {
    traceId: snapshot.traceId,
    spanId: snapshot.spanId,
    parentSpanId: snapshot.parentSpanId,
    name: strings[snapshot.name] ?? "",
    kind: snapshot.kind,
    traceState: snapshot.traceState,
    flags: snapshot.flags,
    attributes: decodeAttributeList(snapshot.attributes, strings),
    droppedAttributesCount: snapshot.droppedAttributesCount,
    links: decodeLinks(snapshot.links, strings),
    droppedLinksCount: snapshot.droppedLinksCount,
    status: snapshot.status,
    startTimeUnixNs: snapshot.startTimeUnixNs,
    endTimeUnixNs: null,
    events: []
  };
}
function applyRecord(span, body, absNs, strings) {
  switch (body.tag) {
    case "SpanStart":
      if (span.startTimeUnixNs === 0n) {
        span.startTimeUnixNs = absNs;
      }
      return;
    case "SpanSnapshot":
      span.traceId = body.val.traceId;
      span.parentSpanId = body.val.parentSpanId;
      span.name = strings[body.val.name] ?? "";
      span.kind = body.val.kind;
      span.traceState = body.val.traceState;
      span.flags = body.val.flags;
      span.attributes = decodeAttributeList(
        body.val.attributes,
        strings
      );
      span.droppedAttributesCount = body.val.droppedAttributesCount;
      span.links = decodeLinks(body.val.links, strings);
      span.droppedLinksCount = body.val.droppedLinksCount;
      span.status = body.val.status;
      span.startTimeUnixNs = body.val.startTimeUnixNs;
      return;
    case "SpanUpdate":
      applyAttributes(span.attributes, body.val.attributes, strings);
      span.droppedAttributesCount += body.val.droppedAttributesCount;
      if (body.val.status) {
        span.status = body.val.status;
      }
      return;
    case "SpanEvent":
      span.events.push({
        name: strings[body.val.name] ?? "",
        timeUnixNs: absNs,
        attributes: decodeAttributeList(
          body.val.attributes,
          strings
        ),
        droppedAttributesCount: body.val.droppedAttributesCount
      });
      return;
    case "SpanEnd":
      span.endTimeUnixNs = absNs;
      if (body.val.status) {
        span.status = body.val.status;
      }
      return;
  }
}
function decodeAttributeList(attributes, strings) {
  const map = /* @__PURE__ */ new Map();
  for (const kv of attributes) {
    const key = strings[kv.key] ?? "";
    try {
      map.set(key, decodeCbor2(toUint8Array(kv.value)));
    } catch {
      continue;
    }
  }
  return map;
}
function applyAttributes(map, attributes, strings) {
  for (const kv of attributes) {
    const key = strings[kv.key] ?? "";
    try {
      map.set(key, decodeCbor2(toUint8Array(kv.value)));
    } catch {
      continue;
    }
  }
}
function decodeLinks(links, strings) {
  return links.map((link) => ({
    traceId: link.traceId,
    spanId: link.spanId,
    traceState: link.traceState,
    attributes: decodeAttributeList(link.attributes, strings),
    droppedAttributesCount: link.droppedAttributesCount
  }));
}
function toOtlpSpan(span) {
  const attributes = mapToOtlpAttributes(span.attributes);
  const events = span.events.map((event) => toOtlpEvent(event));
  const links = span.links.map((link) => toOtlpLink(link));
  const status = span.status ? toOtlpStatus(span.status) : void 0;
  return {
    traceId: hexFromBytes(normalizeBytes(span.traceId)),
    spanId: hexFromBytes(normalizeBytes(span.spanId)),
    parentSpanId: span.parentSpanId ? hexFromBytes(normalizeBytes(span.parentSpanId)) : void 0,
    name: span.name,
    kind: span.kind,
    traceState: span.traceState ?? void 0,
    flags: span.flags || void 0,
    startTimeUnixNano: span.startTimeUnixNs.toString(),
    endTimeUnixNano: span.endTimeUnixNs ? span.endTimeUnixNs.toString() : void 0,
    attributes: attributes.length > 0 ? attributes : void 0,
    droppedAttributesCount: span.droppedAttributesCount || void 0,
    events: events.length > 0 ? events : void 0,
    links: links.length > 0 ? links : void 0,
    droppedLinksCount: span.droppedLinksCount || void 0,
    status
  };
}
function toOtlpEvent(event) {
  const attributes = mapToOtlpAttributes(event.attributes);
  return {
    timeUnixNano: event.timeUnixNs.toString(),
    name: event.name,
    attributes: attributes.length > 0 ? attributes : void 0,
    droppedAttributesCount: event.droppedAttributesCount || void 0
  };
}
function toOtlpLink(link) {
  const attributes = mapToOtlpAttributes(link.attributes);
  return {
    traceId: hexFromBytes(normalizeBytes(link.traceId)),
    spanId: hexFromBytes(normalizeBytes(link.spanId)),
    traceState: link.traceState ?? void 0,
    attributes: attributes.length > 0 ? attributes : void 0,
    droppedAttributesCount: link.droppedAttributesCount || void 0
  };
}
function toOtlpStatus(status) {
  const code = status.code === "OK" /* OK */ ? 1 : status.code === "ERROR" /* ERROR */ ? 2 : 0;
  return {
    code,
    message: status.message ?? void 0
  };
}
function mapToOtlpAttributes(map) {
  const list = [];
  for (const [key, value] of map.entries()) {
    if (value === void 0 || typeof value === "function") {
      continue;
    }
    if (typeof value === "symbol") {
      continue;
    }
    list.push({ key, value: anyValueFromJs(value) });
  }
  return list;
}
function emptyExport(resourceValue) {
  return buildExport([], resourceValue);
}
function buildExport(spans, resourceValue) {
  return {
    resourceSpans: [
      {
        resource: resourceValue,
        scopeSpans: [{ spans }]
      }
    ]
  };
}

export {
  hexFromBytes,
  base64FromBytes,
  anyValueFromCborBytes,
  anyValueFromJs,
  readRangeWireToOtlp
};
//# sourceMappingURL=chunk-UNGPFJ4C.js.map